import { useNavigate } from "react-router-dom";
import PageHeading from "../../Components/PageHeading/PageHeading";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import axios from "axios";
import swal from "sweetalert";
import "./ProductVideoAi.css";
import SamplePdfModal from "../../Components/SamplePdfModal/SamplePdfModal";
import ContentProductVideoAi from "../Gallery/ContentProductVideoAi";
import AiFactsDisplay from "../../Components/AiFacts/AiFactsDisplay";
import { saveAs } from "file-saver";
import { DownloadOutlined } from "@mui/icons-material";

const modelOptions = [
    {
        value: "kling-video-o1",
        label: "Kling Video v1",
        description: "15 credits/sec · Max 10s",
        maxDuration: 10,
        creditCostPerSecond: 15,
    },
    {
        value: "kling-v3.0-std",
        label: "Kling Video v3.0 Standard",
        description: "17 credits/sec · Max 15s",
        maxDuration: 15,
        creditCostPerSecond: 17,
    },
    {
        value: "kling-v3.0-pro",
        label: "Kling Video v3.0 Pro",
        description: "20 credits/sec · Max 15s",
        maxDuration: 15,
        creditCostPerSecond: 20,
    },
];

const durationOptions = [5, 10, 15];

const ProductVideoAi = () => {
    const [productImage, setProductImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [model, setModel] = useState("");
    const [duration, setDuration] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const selectedModelObj = modelOptions.find((m) => m.value === model);
    const maxDuration = selectedModelObj ? selectedModelObj.maxDuration : 15;

    const totalCredits =
        selectedModelObj && duration
            ? selectedModelObj.creditCostPerSecond * Number(duration)
            : 0;

    // cost is 0.012 per credit
    const creditCostUSD = totalCredits * 0.012;

    const fileInputRef = useRef(null);
    const { AIuser, userRefetch, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();

    const creditToLocalString = AIuser?.credits
        ? parseInt(Math.floor(AIuser?.credits)).toLocaleString()
        : "0";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (duration && Number(duration) > maxDuration) {
            setDuration("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProductImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (e) => {
        e.stopPropagation();
        setProductImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleGenerate = async () => {
        if (AIuser.credits < totalCredits) {
            swal({
                text: "You don't have enough credits! Please buy credits to continue.",
                icon: "error",
                buttons: "Buy",
                className: "modal_class_success",
            }).then((value) => {
                if (value) {
                    navigate("/purchase");
                }
            });
            return;
        }

        if (!productImage) {
            swal({
                text: "Please upload a product image.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        if (!model) {
            swal({
                text: "Please select a model.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        if (!duration) {
            swal({
                text: "Please select a duration.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        setIsLoading(true);
        // handleSubmitCredits(totalCredits);
        setUserRefetch(!userRefetch)

        try {
            const formData = new FormData();
            formData.append("credit", totalCredits);
            formData.append("model", model);
            formData.append("duration", duration);
            formData.append("productImage", productImage);

            const response = await fetch(
                "https://backend.whalesai.com/api/v1/product-video-ai/generate",
                {
                    method: "POST",
                    headers: {
                        authorization: `Bearer ${localStorage.getItem("aiUser")}`,
                    },
                    body: formData,
                },
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || "Failed to generate Product Video");
            }

            setIsLoading(false);
            setGeneratedVideoUrl(result.data.videoUrl);
        } catch (error) {
            console.error("Error generating Product Video:", error);
            setIsLoading(false);
            swal({
                text: error.message || "Something went wrong! Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        }
    };

    const handleClear = () => {
        setProductImage(null);
        setImagePreview(null);
        setModel("");
        setDuration("");
        setGeneratedVideoUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const downloadVideo = async () => {
        if (!generatedVideoUrl) return;
        setDownloading(true);
        try {
            const response = await fetch(generatedVideoUrl);
            if (!response.ok) throw new Error("Network response was not ok");
            const blob = await response.blob();
            saveAs(blob, "ProductVideo.mp4");
        } catch (error) {
            console.error("Download failed:", error);
            swal({
                text: "Failed to download the video.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        }
        setDownloading(false);
    };

    const isFormValid = productImage && model && duration;

    return (
        <Fragment>
            <PageHeading
                title={"PRODUCT VIDEO AI"}
                description={
                    "Turn product images into professional AI-powered marketing videos."
                }
                onInfoClick={() => setShowPdfModal(true)}
                infoTooltip="See sample product video"
            />

            <SamplePdfModal pdfModal={showPdfModal} setPdfModal={setShowPdfModal} title="Sample">
                <ContentProductVideoAi />
            </SamplePdfModal>

            <div className="container pb-5"
                style={{
                    maxWidth: "872px",
                    paddingInline: "12px",
                    marginInline: "auto",
                }}
            >
                <div className="container py-5">
                    {/* Credits section */}
                    <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
                        <div>
                            <div>
                                <button className="credits-button">
                                    CREDITS : {creditToLocalString}
                                </button>
                                <button
                                    className="buy-credits-button"
                                    onClick={() => navigate("/purchase")}
                                >
                                    BUY CREDITS
                                </button>
                                <button
                                    onClick={() => navigate("/usage")}
                                    className="money-exchange-button"
                                >
                                    <img src={moneyExchangeIcon} alt="usage" />
                                </button>
                            </div>
                            <p
                                style={{ fontSize: "8px" }}
                                className="text-white mt-2 creditText text-center"
                            >
                                {totalCredits > 0
                                    ? `${totalCredits} Credits (USD ${creditCostUSD.toFixed(2)} based on Credits Package 3)`
                                    : "Select model & duration to see cost"}
                            </p>
                        </div>
                    </div>

                    {/* Step 1: Upload Product Image */}
                    <div className="mb-4 mt-4">
                        <div className="product-video-step-label">Step 1</div>
                        <label className="text-white mb-2" style={{ fontSize: "14px" }}>
                            Upload Product Image <span className="text-danger">*</span>
                        </label>
                        <div
                            className={`product-video-upload-area ${imagePreview ? "has-image" : ""}`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={handleImageChange}
                                style={{ display: "none" }}
                            />
                            {imagePreview ? (
                                <>
                                    <img
                                        src={imagePreview}
                                        alt="Product preview"
                                        className="product-video-preview-img"
                                    />
                                    <button
                                        className="product-video-remove-btn"
                                        onClick={handleRemoveImage}
                                        title="Remove image"
                                    >
                                        ✕
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="product-video-upload-icon">📷</div>
                                    <div className="product-video-upload-text">
                                        Drag & drop your image here or <span>browse</span>
                                    </div>
                                    <div
                                        className="product-video-upload-text mt-1"
                                        style={{ fontSize: "12px" }}
                                    >
                                        Supports JPG, PNG, WEBP
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Step 2: Select Model */}
                    <div className="mb-4">
                        <div className="product-video-step-label">Step 2</div>
                        <label className="text-white mb-2" style={{ fontSize: "14px" }}>
                            Select Model <span className="text-danger">*</span>
                        </label>
                        <div className="product-video-model-grid">
                            {modelOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className={`product-video-model-card ${model === option.value ? "selected" : ""}`}
                                    onClick={() => setModel(option.value)}
                                >
                                    <div className="model-value">{option.label}</div>
                                    <div className="model-label">{option.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Step 3: Select Duration */}
                    <div className="mb-4">
                        <div className="product-video-step-label">Step 3</div>
                        <label className="text-white mb-2" style={{ fontSize: "14px" }}>
                            Select Duration <span className="text-danger">*</span>
                        </label>
                        <div className="product-video-duration-grid">
                            {durationOptions.map((dur) => {
                                const isDisabled = dur > maxDuration;
                                const costForDur = selectedModelObj
                                    ? selectedModelObj.creditCostPerSecond * dur
                                    : null;
                                return (
                                    <div
                                        key={dur}
                                        className={`product-video-duration-card ${duration === String(dur) ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                                        onClick={() => {
                                            if (!isDisabled) setDuration(String(dur));
                                        }}
                                    >
                                        <div className="duration-value">{dur}s</div>
                                        <div className="duration-label">
                                            {isDisabled
                                                ? "Not available for this model"
                                                : costForDur
                                                    ? `${costForDur} credits`
                                                    : `${dur} seconds`}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <p
                        style={{ fontSize: "8px" }}
                        className="text-white mt-2 creditText text-center"
                    >
                        {totalCredits > 0
                            ? `${totalCredits} Credits`
                            : "Select model & duration to see cost"}
                    </p>

                    {/* <AiContentAudit checked={auditChecked} onChange={setAuditChecked} /> */}

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-center align-items-center mb-5">
                        <Button
                            sx={{
                                padding: "8px 30px",
                                marginRight: "10px",
                                color: "#000",
                                background: "#fff",
                                "&:hover": { background: "#fff" },
                                "&:disabled": { background: "#fff" },
                            }}
                            onClick={handleClear}
                            disabled={
                                (!productImage && !model && !duration) || isLoading
                            }
                            variant="contained"
                        >
                            Clear
                        </Button>
                        <Button
                            sx={{
                                padding: "8px 30px",
                                "&:disabled": { background: "gray" },
                            }}
                            onClick={handleGenerate}
                            className="generate-button text-white"
                            disabled={!isFormValid || isLoading}
                            variant="contained"
                        >
                            {isLoading ? "Processing..." : "Generate"}
                        </Button>
                    </div>

                    <div className="animate text-md-center text-lg-center text-center mb-5 d-flex justify-content-center force-small-font">
                        Please do not navigate away from the browser.
                        <br /> Doing so will stop the process.
                    </div>
                    <div className="text-md-center text-white text-lg-center text-center d-flex justify-content-center force-small-font mt-1">
                        While AI strives for accuracy, it may occasionally make mistakes. <br />Please verify any important information.
                    </div>

                    {/* Interesting facts while generating */}
                    {isLoading && <AiFactsDisplay />}

                    <div className="d-flex justify-content-center mt-3">
                        {isLoading && (
                            <div
                                className="spinner-border text-light"
                                role="status"
                                style={{ width: "4rem", height: "4rem" }}
                            >
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        )}
                    </div>

                    {/* Generated Video */}
                    {generatedVideoUrl && (
                        <div className="mt-5 d-flex flex-column align-items-center">
                            <h4 className="text-white mb-3">Generated Video</h4>
                            <div
                                style={{
                                    width: "100%",
                                    maxWidth: "720px",
                                    marginBottom: "24px",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                }}
                            >
                                <video
                                    src={generatedVideoUrl}
                                    controls
                                    autoPlay
                                    muted
                                    style={{
                                        width: "100%",
                                        display: "block",
                                    }}
                                />
                            </div>
                            <Button
                                sx={{
                                    padding: '8px 30px',
                                    margin: '15px auto',
                                    display: 'flex',
                                    gap: '8px',
                                    background: '#2e7d32!important',
                                    color: 'white!important',
                                }}
                                variant="contained"
                                onClick={downloadVideo}
                                disabled={downloading}
                            >
                                <DownloadOutlined />
                                {downloading ? 'Downloading...' : 'Download'}
                            </Button>
                        </div>
                    )}


                </div>
            </div>
        </Fragment>
    );
};

export default ProductVideoAi;